import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { Users, Download, Mail, Phone, MapPin, ShoppingCart, DollarSign, Pencil, Save, X, Send, CheckSquare, Square } from 'lucide-react';

const CustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/customers', { headers: { 'Authorization': `Bearer ${token}` } });
      setCustomers(await res.json());
    } catch { toast({ title: "Error", description: "Failed to load customers.", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const startEdit = (customer) => {
    setEditingCustomer(customer.email);
    setEditForm({
      name: customer.name || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zipCode: customer.zipCode || ''
    });
  };

  const saveEdit = async (email) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/customers/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (!res.ok) throw new Error();
      toast({ title: "Saved!", description: "Customer info updated." });
      setEditingCustomer(null);
      fetchCustomers();
    } catch { toast({ title: "Error", description: "Failed to update customer.", variant: "destructive" }); }
  };

  const toggleEmail = (email) => {
    setSelectedEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const selectAll = () => {
    if (selectedEmails.length === customers.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(customers.map(c => c.email));
    }
  };

  const sendPromoEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim() || selectedEmails.length === 0) return;
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/customers/send-promo-email', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: selectedEmails, subject: emailSubject, message: emailMessage })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast({ title: "Sent!", description: data.message });
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
      setSelectedEmails([]);
    } catch (err) { toast({ title: "Error", description: err.message, variant: "destructive" }); }
    finally { setSendingEmail(false); }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/customers/export-csv', { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success!", description: "Customer list exported." });
    } catch { toast({ title: "Error", description: "Failed to export.", variant: "destructive" }); }
  };

  if (loading) return <p className="text-center py-10" style={{ color: '#736c64' }}>Loading customers...</p>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>Customer Database</h2>
          <p className="text-sm" style={{ color: '#736c64' }}>{customers.length} total customers</p>
        </div>
        <div className="flex gap-2">
          {selectedEmails.length > 0 && (
            <Button
              data-testid="send-email-btn"
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="text-white font-semibold"
              style={{ backgroundColor: '#2563eb' }}
            >
              <Send className="h-4 w-4 mr-2" /> Email ({selectedEmails.length})
            </Button>
          )}
          <Button onClick={handleExportCSV} className="text-white font-semibold" style={{ backgroundColor: '#FF8C19' }}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Promotional Email Form */}
      {showEmailForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4" style={{ borderColor: '#2563eb' }}>
          <h3 className="text-lg font-bold mb-4 flex items-center" style={{ color: '#4a2c1a' }}>
            <Send className="h-5 w-5 mr-2" style={{ color: '#2563eb' }} />
            Send Promotional Email to {selectedEmails.length} customer{selectedEmails.length !== 1 ? 's' : ''}
          </h3>
          <div className="space-y-3 mb-4">
            <Input
              data-testid="email-subject-input"
              placeholder="Email subject (e.g., New Maple Syrup Flavors!)"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            <textarea
              data-testid="email-message-input"
              className="w-full border rounded-lg p-3 min-h-[120px] text-sm"
              style={{ borderColor: '#e5d5c0' }}
              placeholder="Write your promotional message here..."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="send-promo-email-btn"
              onClick={sendPromoEmail}
              disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
              className="text-white font-semibold"
              style={{ backgroundColor: '#2563eb' }}
            >
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
            <Button variant="outline" onClick={() => setShowEmailForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {customers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <Users className="h-16 w-16 mx-auto mb-4" style={{ color: '#e5d5c0' }} />
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#4a2c1a' }}>No customers yet</h3>
          <p style={{ color: '#736c64' }}>Customers will appear here when they place orders.</p>
        </div>
      ) : (
        <div>
          {/* Select All */}
          <div className="flex items-center gap-2 mb-3 px-1">
            <button onClick={selectAll} className="flex items-center gap-2 text-sm font-medium" style={{ color: '#4a2c1a' }}>
              {selectedEmails.length === customers.length
                ? <CheckSquare className="h-5 w-5" style={{ color: '#FF8C19' }} />
                : <Square className="h-5 w-5" style={{ color: '#736c64' }} />}
              Select All
            </button>
          </div>

          <div className="space-y-3">
            {customers.map((customer) => (
              <div
                key={customer.email}
                data-testid={`customer-row-${customer.email}`}
                className="bg-white rounded-lg shadow-sm overflow-hidden border-l-4 transition-all hover:shadow-md"
                style={{ borderColor: selectedEmails.includes(customer.email) ? '#2563eb' : '#e5d5c0' }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Checkbox */}
                      <button onClick={() => toggleEmail(customer.email)}>
                        {selectedEmails.includes(customer.email)
                          ? <CheckSquare className="h-5 w-5" style={{ color: '#2563eb' }} />
                          : <Square className="h-5 w-5" style={{ color: '#736c64' }} />}
                      </button>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FF8C1920' }}>
                        <Users className="h-5 w-5" style={{ color: '#FF8C19' }} />
                      </div>
                      <div>
                        <h3 className="font-bold" style={{ color: '#4a2c1a' }}>{customer.name || 'No name'}</h3>
                        <p className="text-sm" style={{ color: '#736c64' }}>{customer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>{customer.totalOrders} orders</p>
                        <p className="text-lg font-bold" style={{ color: '#FF8C19' }}>${customer.totalSpent?.toFixed(2) || '0.00'}</p>
                      </div>
                      {customer.subscribedToNewsletter && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white hidden sm:inline" style={{ backgroundColor: '#22c55e' }}>
                          Subscribed
                        </span>
                      )}
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setExpandedCustomer(expandedCustomer === customer.email ? null : customer.email)}
                      >
                        {expandedCustomer === customer.email ? 'Hide' : 'View'}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedCustomer === customer.email && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#e5d5c0' }}>
                      {editingCustomer === customer.email ? (
                        /* Edit Form */
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium" style={{ color: '#736c64' }}>Name</label>
                              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                            </div>
                            <div>
                              <label className="text-xs font-medium" style={{ color: '#736c64' }}>Phone</label>
                              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                            </div>
                            <div>
                              <label className="text-xs font-medium" style={{ color: '#736c64' }}>Address</label>
                              <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
                            </div>
                            <div>
                              <label className="text-xs font-medium" style={{ color: '#736c64' }}>City</label>
                              <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                            </div>
                            <div>
                              <label className="text-xs font-medium" style={{ color: '#736c64' }}>State</label>
                              <Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} />
                            </div>
                            <div>
                              <label className="text-xs font-medium" style={{ color: '#736c64' }}>Zip Code</label>
                              <Input value={editForm.zipCode} onChange={(e) => setEditForm({ ...editForm, zipCode: e.target.value })} />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => saveEdit(customer.email)} className="text-white" style={{ backgroundColor: '#22c55e' }}>
                              <Save className="h-4 w-4 mr-1" /> Save
                            </Button>
                            <Button variant="outline" onClick={() => setEditingCustomer(null)}>
                              <X className="h-4 w-4 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold" style={{ color: '#4a2c1a' }}>Contact Information</h4>
                              <Button
                                data-testid={`edit-customer-${customer.email}`}
                                variant="outline" size="sm"
                                onClick={() => startEdit(customer)}
                                style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
                              >
                                <Pencil className="h-3 w-3 mr-1" /> Edit
                              </Button>
                            </div>
                            <div className="space-y-2 text-sm">
                              <p className="flex items-center" style={{ color: '#736c64' }}>
                                <Mail className="h-4 w-4 mr-2" />
                                <a href={`mailto:${customer.email}`} className="hover:underline">{customer.email}</a>
                              </p>
                              {customer.phone && (
                                <p className="flex items-center" style={{ color: '#736c64' }}>
                                  <Phone className="h-4 w-4 mr-2" />
                                  <a href={`tel:${customer.phone}`} className="hover:underline">{customer.phone}</a>
                                </p>
                              )}
                              {customer.address && (
                                <p className="flex items-start" style={{ color: '#736c64' }}>
                                  <MapPin className="h-4 w-4 mr-2 mt-0.5" />
                                  <span>{customer.address}<br />{customer.city}, {customer.state} {customer.zipCode}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2" style={{ color: '#4a2c1a' }}>Customer Stats</h4>
                            <div className="space-y-2 text-sm">
                              <p className="flex items-center" style={{ color: '#736c64' }}>
                                <ShoppingCart className="h-4 w-4 mr-2" />
                                Total Orders: <strong className="ml-1">{customer.totalOrders}</strong>
                              </p>
                              <p className="flex items-center" style={{ color: '#736c64' }}>
                                <DollarSign className="h-4 w-4 mr-2" />
                                Total Spent: <strong className="ml-1" style={{ color: '#FF8C19' }}>${customer.totalSpent?.toFixed(2) || '0.00'}</strong>
                              </p>
                              <p className="text-xs" style={{ color: '#736c64' }}>Source: {customer.source}</p>
                              {customer.lastOrderDate && (
                                <p className="text-xs" style={{ color: '#736c64' }}>Last Order: {new Date(customer.lastOrderDate).toLocaleDateString()}</p>
                              )}
                              <p className="text-xs" style={{ color: '#736c64' }}>Joined: {new Date(customer.createdAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersTab;
